import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowLeft } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/portal";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) navigate(from, { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#0A0A0A]">
        <img src="https://images.unsplash.com/photo-1552652893-2aa10a0ab4df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <h2 className="font-heading font-black text-white text-4xl tracking-tighter leading-tight">
            Start systemizing<br />your dealership today.
          </h2>
          <p className="text-white/60 mt-4 max-w-sm">Create an account to manage your purchases and downloads.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-10">
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
          <h1 className="font-heading font-black text-3xl tracking-tight">Create your account</h1>
          <p className="text-muted-foreground mt-2">It only takes a moment.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            {error && <div className="bg-destructive/10 text-destructive text-sm px-4 py-3 border border-destructive/20" data-testid="register-error">{error}</div>}
            <div>
              <label className="text-sm font-medium block mb-2">Full name</label>
              <input required value={form.name} onChange={set("name")} data-testid="register-name"
                className="w-full border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Email</label>
              <input type="email" required value={form.email} onChange={set("email")} data-testid="register-email"
                className="w-full border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Password</label>
              <input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password"
                className="w-full border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full bg-primary text-white py-3.5 font-semibold btn-lift disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : "Create account"}
            </button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            Already have an account? <Link to="/login" className="text-primary font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
